/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { CourierType } from '@well/courier-shared/domain';
import {
  greaterThan,
  maxStringLengthValidator,
  minNumber, minStringLengthValidator,
  requiredNumber,
  requiredObject,
  requiredString,
  ValidatorSchema
} from '@irian-ro/util-forms';
import { ShipmentRequestDtoModel } from '../shipment-request-dto.model';
import { TimeWindowDtoModel } from '@well/common-libs/time-management/domain-model';
import { ValidationErrors } from '@angular/forms';
import { isDateTimeWithTimezoneInPast } from '@irian-ro/date-utils';
import { arePickupAndDeliveryTimeWindowsValid } from './abstract-shipment-common.validators';
import { PackageDtoModel } from '@well/delivery-libs/packages/domain';
import { ShipmentConfig } from './shipment-request-dto.validators';
import { HandoverDtoModel } from '@well/delivery-libs/handover/domain';
import { ProductCustomsClearanceDtoModel } from '@well/product-customs-clearance/domain';
import { Signal } from '@angular/core';
import { MAXIMUM_DEFAULT_STRING_INPUT_LENGTH } from '@irian-ro/constants';

export const isInternationalShipment = (pickup?: HandoverDtoModel | null, delivery?: HandoverDtoModel | null) => {
  const pickupCountry = getCountry(pickup);
  const deliveryCountry = getCountry(delivery);
  if (pickupCountry != null && deliveryCountry != null) {
    return pickupCountry !== deliveryCountry;
  }
  return false;
}

export const isImportShipment = (pickup?: HandoverDtoModel | null, companyCountry?: string | null) => {
  const pickupCountry = getCountry(pickup);
  if (pickupCountry != null && companyCountry != null) {
    return pickupCountry !== companyCountry;
  }
  return false;
}

export const getCountry = (handover?: HandoverDtoModel | null) => {
  if (handover == null) {
    return null;
  }
  if (handover.address != null) {
    return handover.address.country;
  }
  if (handover.namedLocationVO != null) {
    return handover.namedLocationVO.country;
  }
  return null;
}

export const shouldHaveProducts = (externalVendors: Signal<CourierType[]>, value?: ShipmentRequestDtoModel | null) =>
  value ? (isInternationalShipment(value.pickup, value.delivery) || externalVendors().includes(CourierType.DHL)) : false

export const shouldHavePackageTags = (customerCountry: string, externalVendors: Signal<CourierType[]>, value?: any) =>
  value ? externalVendors().includes(CourierType.UPS) && isInternationalShipment(value.pickup, value.delivery) && isImportShipment(value.pickup, customerCountry) : false

export const productsWeightIsBiggerThanPackagesWeight = (packages: PackageDtoModel[], products: ProductCustomsClearanceDtoModel[]) => {
  const packagesWeight = packages.reduce((sum, pkg) => {
    const weight = pkg.weight ?? 0;
    const pieces = pkg.pieces ?? 1;
    return sum + weight * pieces;
  }, 0);
  const productsWeight = products.length > 0 ? products.reduce((sum, pkg) => {
    return sum + (pkg.totalWeight ?? 0);
  }, 0) : 0
  return productsWeight > packagesWeight
}

export const isFedex = (externalVendors: Signal<CourierType[]>) => externalVendors().includes(CourierType.FED_EX);
export const isDhl = (externalVendors: Signal<CourierType[]>) => externalVendors().includes(CourierType.DHL);
export const isFedexOrDhl = (externalVendors: Signal<CourierType[]>) => isFedex(externalVendors) || isDhl(externalVendors);

export class ShipmentRequestDtoWithCourierValidators {
  config : ShipmentConfig;

  externalVendors: Signal<CourierType[]>

  customerCountry: string

  constructor(config: ShipmentConfig, customerCountry: string, externalVendors: Signal<CourierType[]>) {
    this.config = config;
    this.customerCountry = customerCountry;
    this.externalVendors = externalVendors;
  }

  get(): ValidatorSchema<ShipmentRequestDtoModel> {
    return shipmentRequestDtoWithCourier(this.config, this.customerCountry, this.externalVendors);
  }
}

export const shipmentRequestDtoWithCourier = (config: ShipmentConfig, customerCountry: string, externalVendors: Signal<CourierType[]>): ValidatorSchema<ShipmentRequestDtoModel> => {
  return {
    pickup: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      contact: {
        name: [
          ...(isFedex(externalVendors) ? [minStringLengthValidator(3)] : []),
          requiredString,
          maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)
        ],
        phoneNumber: [
          ...(isDhl(externalVendors) ? [minStringLengthValidator(4)] : []),
          requiredString,
          maxStringLengthValidator(20)
        ],
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        },
        {
          field: 'contact', condition : (value: any) => !!value?.namedLocationPartnerContactVO
        }
      ],
    },
    delivery: {
      address: {
        city: [requiredString],
        postalCode: [requiredString],
      },
      namedLocationVO: requiredObject,
      timeWindowDto: (dateWindow: TimeWindowDtoModel | null | undefined): ValidationErrors | null => {
        return {
          ...[
            requiredObject(dateWindow?.timeWindowStart, 'startRequired'),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowStart, config.dateOffset()),
            isDateTimeWithTimezoneInPast(dateWindow?.timeWindowEnd, config.dateOffset())
          ]
            .filter((error) => !!error)
            .reduce((acc, error) => ({ ...acc, ...error }), {}),
        };
      },
      contact: {
        name: [
          ...(isFedex(externalVendors) ? [minStringLengthValidator(3)] : []),
          requiredString,
          maxStringLengthValidator(MAXIMUM_DEFAULT_STRING_INPUT_LENGTH)
        ],
        phoneNumber: [
          ...(isDhl(externalVendors) ? [minStringLengthValidator(4)] : []),
          requiredString,
          maxStringLengthValidator(20)
        ],
      },
      $omit: [
        {
          field: 'namedLocationVO',
          condition: (value: any) => !!value?.address,
        },
        {
          field: 'address',
          condition: (value: any) => !!value?.namedLocationVO,
        },
        {
          field: 'contact', condition : (value: any) => !!value?.namedLocationPartnerContactVO
        }
      ],
    },
    packages: {
      height: [requiredNumber, minNumber(1)],
      width: [requiredNumber, minNumber(1)],
      length: [requiredNumber, minNumber(1)],
      weight: [requiredNumber, greaterThan(0)],
      pieces: [requiredNumber, minNumber(1)]
    },
    productCustomsClearanceDTOS: {
      commodityDescription: [requiredString],
      quantity: isFedexOrDhl(externalVendors) ? [requiredNumber, minNumber(1)] : undefined,
      unitPrice: [requiredObject],
      customsValue: [requiredObject],
      countryOfManufacture: isFedexOrDhl(externalVendors) ? [requiredString] : undefined,
      totalWeight: [
        ...(isFedexOrDhl(externalVendors) ? [requiredNumber] : []),
        greaterThan(0)
      ],
    },
    $cross: [
      (shipment: any) => arePickupAndDeliveryTimeWindowsValid(shipment),
      (shipment: any) => {
        return shipment?.packages && Object.keys(shipment.packages).length ? null : { packageRequired: true };
      },
      (shipment: any) => {
        return shipment?.pickup && (shipment.pickup.namedLocationVO || shipment.pickup.address)
          ? null
          : { pickupLocationRequired: true };
      },
      (shipment: any) => {
        return shipment?.delivery && (shipment.delivery.namedLocationVO || shipment.delivery.address)
          ? null
          : { deliveryLocationRequired: true };
      },
      (shipment) => {
        const totalPackagesForCurrentShipment = (Object.values(shipment?.packages || {})).reduce((acc, pkg) => acc + (pkg?.pieces || 0), 0);
        return totalPackagesForCurrentShipment > config.availablePackageCount() ? { packageCountExceeded: true } : null;
      },
      (shipment: any) => {
        if(shouldHaveProducts(externalVendors, shipment)) {
          if(shipment?.productCustomsClearanceDTOS) {
            const raw = shipment.productCustomsClearanceDTOS as Record<string, any>;
            const { productCustomsClearancesCurrencyCode, ...products } = raw;
            return (Object.keys(products).length > 0) ? null : { productCustomsClearanceDTOSRequired: true }
          }
          else {
            return { productCustomsClearanceDTOSRequired: true }
          }
        }
        return null;
      },
      (shipment: any) => {
        if(shouldHavePackageTags(customerCountry, externalVendors, shipment)) {
          if(shipment?.packages) {
            const tagsPerPackageGroup = {} as Record<string, boolean | null>;
            Object.entries(shipment.packages).forEach(([key, value]) => {
              const pckGroup = value as PackageDtoModel
              tagsPerPackageGroup[key] = (pckGroup && pckGroup.tags && pckGroup.tags.length === 0) ? true : null
            })
            const pckGroups = Object.values(tagsPerPackageGroup)
            if(tagsPerPackageGroup === null || pckGroups.length === 0 || pckGroups.every(pckGroup => pckGroup === null)) {
              return null
            }
            return {tagsPerPackageGroup: true};
          }
          else {
            return null
          }
        }
        return null;
      },
      (shipment: any) => {
      if(shipment?.packages && shipment?.productCustomsClearanceDTOS) {
        const raw = shipment.productCustomsClearanceDTOS as Record<string, any>;
        const { productCustomsClearancesCurrencyCode, ...products } = raw;
        if(productsWeightIsBiggerThanPackagesWeight(Object.values(shipment.packages), Object.values(products))) {
          return {productsWeightIsBiggerThanPackagesWeight: true}
        }
        return null
      }
      return null
      }
    ],
    $omit: [
      {
        field: 'productCustomsClearanceDTOS',
        condition: (value: any) => !shouldHaveProducts(externalVendors, value)
      }
    ]
  }
}
